/* global runtime, mergeState, safeArray, num, getReportsModel, buildLedgerLines, buildVatFilingModel, taxReconRound, taxReconTaxPayments, taxReconBankTraces, taxReconBankCoverage, taxReconWithholdingClaims, taxReconAgaClaims, taxReconVatClaim, taxReconTrekkRows, taxReconAgaSection, taxReconVatSection, taxReconCompanyTaxSection, taxReconPaymentRows, taxReconItems, taxReconSources, taxReconChecks */
// SKATT OG OFFENTLIGE AVGIFTER — lesemodell for byråets skatteskjerm
//
// Skatteområdet er det området der saldoen alene sier minst om noe er i orden.
// 2600 og 2770 kan stemme med seg selv og likevel være feil: forpliktelsen
// oppstår av lønnskjøringen, forfallet er lovbestemt, og oppgjøret skjer i
// banken. Tre hendelser, ikke én saldo — og de kan svikte hver for seg.
//
// Modellen er READ-ONLY og regner ikke ut noe fagtall selv:
//   forskuddstrekk og AGA  → `PlummePayrollObligations` (samme modell som lønn)
//   MVA-oppgjøret          → `buildVatFilingModel` (samme modell som innlevering)
//   bokføringen            → `buildLedgerLines` og saldobalansen
//
// Det appen ikke har, later den ikke som at den har. Plumme henter INGEN
// kravoversikt fra Skatteetaten og ingen KID. Kravene her er foretakets egne
// beregnede forpliktelser med lovbestemt forfall. Det er en dårligere
// avstemming enn mot et hentet krav — men en ærlig en, og skjermen sier hva den
// faktisk sammenlikner.
//
// Kravene, betalingene og seksjonene ligger i backend-tax-recon-parts.jsx.
// Denne fila eier kontrollkontoene, sammenstillingen og kontrollene.

// Kontrollkontoene for offentlige forpliktelser. Rollen avgjør hvilket grunnlag
// kontoen kan sammenliknes med — en konto uten rolle har ikke noe beregnet
// grunnlag, og da er differansen `null` og ikke null kroner.
const TAX_RECON_ACCOUNTS = [
  { code: '2600', role: 'forskuddstrekk' },
  { code: '2610', role: 'utleggstrekk' },
  { code: '2770', role: 'aga' },
  { code: '2780', role: 'aga_paalopt' },
  { code: '2740', role: 'mva_oppgjor' },
  { code: '2500', role: 'betalbar_skatt' },
  { code: '2510', role: 'utsatt_skatt' },
  { code: '2540', role: 'forskuddsskatt' },
  { code: '2950', role: 'offentlige_avgifter' },
];

// Rollekodene er stabile; teksten oversettes og leses ved bruk (ikke frosset ved
// modul-innlasting). `TAX_RECON_ROLE_NOTE[role]` beholder sin indekserbare form.
const TAX_RECON_ROLE_NOTE_KEY = {
  forskuddstrekk: 'withholding',
  utleggstrekk: 'garnishment',
  aga: 'aga',
  aga_paalopt: 'agaAccrued',
  mva_oppgjor: 'vatSettlement',
  betalbar_skatt: 'taxPayable',
  utsatt_skatt: 'deferredTax',
  forskuddsskatt: 'advanceTax',
  offentlige_avgifter: 'publicFees',
};
const TAX_RECON_ROLE_NOTE = new Proxy({}, {
  get: (target, prop) => (typeof prop === 'string' && Object.prototype.hasOwnProperty.call(TAX_RECON_ROLE_NOTE_KEY, prop)
    ? t('rcs.role.' + TAX_RECON_ROLE_NOTE_KEY[prop])
    : undefined),
});

// Kostnadskontoene skatteområdet leser. De er resultat, ikke balanse, og ligger
// derfor ikke i avstemmingsområdet — men uten dem kan ikke en avgift som er
// riktig kostnadsført og feil gjeldsført skilles fra en som er feil beregnet.
const TAX_RECON_AGA_COST_ACCOUNT = '5400';
const TAX_RECON_TAX_COST_ACCOUNT = '8300';

const taxReconMonthRange = (periodId) => {
  const [yearText, monthText] = String(periodId || '').split('-');
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) return null;
  const pad = (value) => String(value).padStart(2, '0');
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  return {
    year,
    monthIndex,
    from: year + '-' + pad(monthIndex + 1) + '-01',
    to: year + '-' + pad(monthIndex + 1) + '-' + pad(lastDay),
  };
};

// Arbeidsgiveravgiften påløper gjennom terminen. Bokføringen som skal
// sammenliknes med terminens beregnede avgift er derfor bevegelsen fra
// terminens start til periodeslutt — ikke måneden alene, som ville sammenliknet
// halve terminens føringer med hele terminens avgift.
const taxReconAgaTermRange = (range) => {
  const termStartMonth = Math.floor(range.monthIndex / 2) * 2;
  const pad = (value) => String(value).padStart(2, '0');
  return { from: range.year + '-' + pad(termStartMonth + 1) + '-01', to: range.to };
};


// ------------------------------------------------------------------
// Kontrollkontoene i hovedboken
// ------------------------------------------------------------------

const taxReconControlAccounts = ({ state, range, areaAccounts, basisByRole }) => {
  const reports = typeof getReportsModel === 'function'
    ? getReportsModel({
      year: range.year, months: [range.monthIndex, range.monthIndex],
      includeChart: false, includeResult: false,
    })
    : null;
  const trial = new Map();
  for (const group of safeArray(reports?.trial)) {
    for (const account of safeArray(group.accounts)) trial.set(String(account.nr || ''), account);
  }

  // Kontoene som skal med: de faste kontrollkontoene, og kontoene byrået selv
  // har lagt til skatteområdet. En importert kontoplan kan bruke andre numre,
  // og da er det området som bestemmer.
  const wanted = new Map();
  for (const entry of TAX_RECON_ACCOUNTS) wanted.set(entry.code, entry.role);
  for (const account of safeArray(areaAccounts)) {
    const code = String(account.accountId || '');
    if (code && !wanted.has(code)) wanted.set(code, null);
  }

  const codes = [...wanted.keys()];
  const lines = codes.length && typeof buildLedgerLines === 'function'
    ? buildLedgerLines({ from: range.from, to: range.to, accountCodes: codes, state })
    : [];
  const movement = new Map();
  for (const line of lines) {
    const entry = movement.get(line.accountCode) || { debet: 0, kredit: 0 };
    if (num(line.amount) >= 0) entry.debet += num(line.amount);
    else entry.kredit += -num(line.amount);
    movement.set(line.accountCode, entry);
  }

  const rows = codes.map((code) => {
    const role = wanted.get(code);
    const account = trial.get(code) || null;
    const entry = movement.get(code) || { debet: 0, kredit: 0 };
    const areaAccount = safeArray(areaAccounts).find((row) => String(row.accountId) === code) || null;
    const closing = account
      ? taxReconRound(account.ut)
      : areaAccount ? taxReconRound(num(areaAccount.bookedBalance)) : 0;
    const opening = account
      ? taxReconRound(account.inn)
      : areaAccount ? taxReconRound(num(areaAccount.openingBalance)) : 0;
    const basis = role && Object.prototype.hasOwnProperty.call(basisByRole, role)
      ? basisByRole[role]
      : null;
    // Gjeldskontoene står med kredit i hovedboken, mens grunnlaget fra
    // lønns- og MVA-modellen er et positivt beløp. Sammenlikningen skjer derfor
    // på absoluttverdien — fortegnet er kontoplanens, ikke en feil.
    const difference = basis === null ? null : taxReconRound(Math.abs(closing) - basis);
    const empty = !opening && !closing && !entry.debet && !entry.kredit;
    return {
      code,
      name: account ? (account.navn || account.name || '') : (areaAccount?.accountName || ''),
      role,
      note: role ? TAX_RECON_ROLE_NOTE[role] : t('rcs.role.addedByFirm'),
      opening,
      debet: taxReconRound(entry.debet),
      kredit: taxReconRound(entry.kredit),
      closing,
      basis,
      difference,
      status: empty && basis === null ? 'ikke_relevant'
        : difference === null ? 'ikke_sammenliknet'
          : Math.abs(difference) > 0.5 ? 'differanse' : 'stemmer',
      inArea: Boolean(areaAccount),
    };
  }).sort((left, right) => left.code.localeCompare(right.code));

  return { rows, lines };
};

// Bevegelsen på én konto i et gitt tidsrom. Brukes til terminbevegelsen for
// arbeidsgiveravgift og til kostnadskontoene, som ikke ligger i området.
const taxReconMovement = ({ state, from, to, code, credit }) => {
  if (typeof buildLedgerLines !== 'function') return null;
  const lines = buildLedgerLines({ from, to, accountCodes: [code], state });
  if (!lines.length) return null;
  const sum = lines.reduce((total, line) => total + num(line.amount), 0);
  return taxReconRound(credit ? -sum : sum);
};


// ------------------------------------------------------------------
// Hele skattebildet for én periode
// ------------------------------------------------------------------

const buildTaxReconciliationView = ({ periodId, accounts = [], state: suppliedState } = {}) => {
  const range = taxReconMonthRange(periodId);
  const empty = {
    periodId, from: '', to: '', claims: [], items: [], controlAccounts: [], ledgerLines: [],
    trekkRows: [], payments: [], sources: [], checks: [], counts: {}, main: null,
    aga: null, vat: null, companyTax: null, term: null,
  };
  if (!range) return empty;
  const state = suppliedState || mergeState(runtime.appState);

  // Banklinjene som allerede er matchet mot en registrert betaling. Dette er den
  // ene eksterne kilden omraadet har: kravet er foretakets eget tall, men at
  // pengene forlot kontoen staar i bankutskriften.
  const traces = taxReconBankTraces(state);
  const bankCoverage = taxReconBankCoverage(state, range);
  const payments = taxReconTaxPayments(state, range.year, traces);
  const agaTerm = taxReconAgaTermRange(range);
  // Bokført arbeidsgiveravgiftsgjeld hittil i terminen. Kredit er gjeldssiden —
  // motposten er kostnaden, ikke avgiften.
  const agaBooked = taxReconMovement({ state, from: agaTerm.from, to: agaTerm.to, code: '2770', credit: true });
  const agaCost = taxReconMovement({
    state, from: agaTerm.from, to: agaTerm.to, code: TAX_RECON_AGA_COST_ACCOUNT, credit: false,
  });
  const taxCost = taxReconMovement({
    state, from: range.year + '-01-01', to: range.to, code: TAX_RECON_TAX_COST_ACCOUNT, credit: false,
  });

  const filing = typeof buildVatFilingModel === 'function' ? buildVatFilingModel({ year: range.year }) : null;
  const term = safeArray(filing?.terms).find(
    (entry) => range.monthIndex >= entry.months[0] && range.monthIndex <= entry.months[1],
  ) || null;

  const withholdingClaims = taxReconWithholdingClaims({ state, range, payments, bankCoverage });
  const agaClaims = taxReconAgaClaims({ state, range, payments, agaBooked, bankCoverage });

  // Kontrollkontoene bygges to ganger: først uten MVA-grunnlaget, fordi
  // MVA-kravet trenger saldoen på oppgjørskontoen for å kunne si om oppgjøret
  // er gjort. Deretter med alle grunnlagene på plass.
  const preliminary = taxReconControlAccounts({ state, range, areaAccounts: accounts, basisByRole: {} });
  const settlementAccount = preliminary.rows.find((row) => row.role === 'mva_oppgjor') || null;
  const vatClaim = taxReconVatClaim({
    term, range, traces, bankCoverage,
    settlementBalance: settlementAccount ? settlementAccount.closing : null,
  });

  const claims = [...withholdingClaims, ...agaClaims, ...(vatClaim ? [vatClaim] : [])];

  const basisByRole = {
    forskuddstrekk: taxReconRound(withholdingClaims.reduce((sum, claim) => sum + claim.openAtPeriodEnd, 0)),
    aga: taxReconRound(agaClaims.reduce((sum, claim) => sum + claim.openAtPeriodEnd, 0)),
    // Oppgjørskontoen skal stå med det som er LEVERT og ikke gjort opp. Et
    // utkast er ikke et krav, og skal derfor ikke stå der.
    mva_oppgjor: taxReconRound(safeArray(filing?.terms)
      .filter((entry) => entry.status === 'levert')
      .reduce((sum, entry) => sum + Math.max(0, Math.abs(Math.round(entry.netVat)) - num(entry.betalt)), 0)),
  };
  const controlAccounts = taxReconControlAccounts({ state, range, areaAccounts: accounts, basisByRole });
  const accountsByCode = new Map(controlAccounts.rows.map((row) => [row.code, row]));

  const paymentRows = taxReconPaymentRows({ claims, lines: controlAccounts.lines, range });
  const items = taxReconItems({ claims, payments: paymentRows });

  const runs = safeArray(state.payrollRuns).filter((run) => {
    const period = String(run?.period || '').slice(0, 7);
    if (period) return period === String(periodId);
    const paid = String(run?.paymentDate || '').slice(0, 10);
    return Boolean(paid) && paid >= range.from && paid <= range.to;
  });

  const flagCount = (flag) => items.filter((item) => item.flags.includes(flag)).length;
  const counts = {
    claims: claims.length,
    flagged: items.length,
    betalingMangler: flagCount('betaling_mangler'),
    delvisBetalt: flagCount('delvis_betalt'),
    betaltForSent: flagCount('betalt_for_sent'),
    betaltEtterPeriode: flagCount('betalt_etter_periode'),
    bokforingsdifferanse: flagCount('bokforingsdifferanse'),
    ikkeBokfort: flagCount('ikke_bokfort'),
    ikkeMeldt: flagCount('ikke_meldt'),
    mvaIkkeLevert: flagCount('mva_ikke_levert'),
    direktefort: flagCount('direktefort'),
    utenBankdekning: flagCount('uten_bankdekning'),
    accountDeviations: controlAccounts.rows.filter((row) => row.status === 'differanse').length,
    dueSoon: claims.filter((claim) => claim.open > 0.005 && claim.dueDate
      && claim.dueDate > range.to
      && claim.dueDate <= new Date(new Date(range.to + 'T00:00:00Z').getTime() + 14 * 86400000)
        .toISOString().slice(0, 10)).length,
    overdue: claims.filter((claim) => claim.open > 0.005 && claim.dueDate && claim.dueDate <= range.to).length,
  };

  // Hovedavstemmingen: bokført gjeld på kontrollkontoene mot de beregnede
  // kravene. Bare kontoene som HAR et grunnlag teller med — en konto uten
  // ekstern kilde ville ellers dratt differansen med et tall ingen har
  // sammenliknet.
  const compared = controlAccounts.rows.filter((row) => row.basis !== null);
  const bookedTotal = taxReconRound(compared.reduce((sum, row) => sum + Math.abs(row.closing), 0));
  const claimTotal = taxReconRound(compared.reduce((sum, row) => sum + row.basis, 0));
  // Forklarte poster er avstemmingens egne, fra kontoene i området. De er
  // byråets forklaringer på differansen, ikke en ny beregning.
  const explained = taxReconRound(safeArray(accounts).reduce(
    (sum, account) => sum + safeArray(account.explainedItems).reduce((inner, item) => inner + num(item?.amount), 0), 0,
  ));
  const main = {
    booked: bookedTotal,
    claims: claimTotal,
    difference: taxReconRound(bookedTotal - claimTotal),
    explained,
    unexplained: taxReconRound(bookedTotal - claimTotal - explained),
    comparedAccounts: compared.map((row) => row.code),
    openClaims: claims.filter((claim) => claim.open > 0.005).length,
    dueSoon: counts.dueSoon,
    overdue: counts.overdue,
    paidInPeriod: taxReconRound(payments
      .filter((payment) => payment.date >= range.from && payment.date <= range.to)
      .reduce((sum, payment) => sum + payment.amount, 0)),
  };

  return {
    periodId,
    from: range.from,
    to: range.to,
    year: range.year,
    term,
    claims,
    items,
    controlAccounts: controlAccounts.rows,
    ledgerLines: controlAccounts.lines,
    trekkRows: taxReconTrekkRows({ claims, range }),
    payments: paymentRows,
    main,
    aga: taxReconAgaSection({ claims, range, agaBooked, agaCost }),
    vat: taxReconVatSection({
      claim: vatClaim, term, settlementBalance: settlementAccount ? settlementAccount.closing : null,
    }),
    companyTax: taxReconCompanyTaxSection({ accountsByCode, taxCost }),
    runs,
    bankCoverage,
    sources: taxReconSources({ state, range, claims, controlAccounts, term, runs, bankCoverage }),
    counts,
    checks: taxReconChecks({ claims, controlAccounts, counts, term, range, runs, bankCoverage }),
  };
};

Object.assign(window, {
  buildTaxReconciliationView, taxReconMonthRange, taxReconAgaTermRange,
  taxReconControlAccounts, taxReconMovement,
  TAX_RECON_ACCOUNTS, TAX_RECON_ROLE_NOTE,
});
